// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import {
  Bot, Server, KeyRound, Boxes, SlidersHorizontal, Activity, PlugZap,
  Loader2, Save, RotateCcw, AlertTriangle, Check, ListChecks,
} from "lucide-react";

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1";

const SOURCE_LABEL = {
  explicit: { zh: "页面 / 环境变量显式配置", en: "Explicitly configured" },
  "emergent-default": { zh: "按 Emergent 密钥走内置网关", en: "Built-in gateway (Emergent key)" },
  "provider-default": { zh: "未指定，走 SDK 默认", en: "Not set (SDK default)" },
};

const KEY_SOURCE_LABEL = {
  db: { zh: "后台", en: "db" },
  env: { zh: "环境变量", en: "env" },
  none: { zh: "未配置", en: "not set" },
};

const Section = ({ title, icon: Icon, badge, hint, children, testId, bodyClass = "p-4" }) => (
  <section data-testid={testId} className="rounded-lg border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
      <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}{title}
      </h2>
      {badge}
      {hint && <span className="text-xs text-slate-400 ml-auto">{hint}</span>}
    </header>
    <div className={bodyClass}>{children}</div>
  </section>
);

const StatusBadge = ({ ok, zh, okText, badText }) => (
  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
    {ok ? (okText || (zh ? "已配置" : "Configured")) : (badText || (zh ? "未配置" : "Not configured"))}
  </span>
);

const Field = ({ label, hint, children, testId }) => (
  <div className="grid gap-1.5 sm:grid-cols-[170px_1fr] sm:items-center" data-testid={testId}>
    <span className="text-xs text-slate-500 min-w-0">
      <span className="block truncate">{label}</span>
      {hint && <span className="block text-[11px] text-slate-400 truncate">{hint}</span>}
    </span>
    {children}
  </div>
);

/* AI 配置：渠道网关 / 密钥 / 模型映射 / 参数，DB 覆盖环境变量，保存即时生效。 */
export default function AISettings() {
  const { api, lang: ui } = useApp();
  const zh = ui === "zh";
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/ai/overview");
      setOv(data);
      setDraft({
        baseUrl: data.baseUrlOverridden ? data.baseUrl : "",
        key: "",
        models: Object.fromEntries(data.models.map((m) => [m.purpose, m.overridden ? m.value : ""])),
        temperature: data.params?.temperature || "",
        maxTokens: data.params?.maxTokens || "",
      });
    } catch (e) {
      setOv(null);
      setErr(e?.detailText || e.message || "Failed");
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!ov || !draft) return false;
    const base = draft.baseUrl.trim().replace(/\/+$/, "");
    if (base !== (ov.baseUrlOverridden ? ov.baseUrl : "")) return true;
    if (draft.key.trim()) return true;
    if (ov.models.some((m) => (draft.models[m.purpose] || "").trim() !== (m.overridden ? m.value : ""))) return true;
    if (String(draft.temperature).trim() !== String(ov.params?.temperature || "").trim()) return true;
    if (String(draft.maxTokens).trim() !== String(ov.params?.maxTokens || "").trim()) return true;
    return false;
  }, [ov, draft]);

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    const body = {};
    const base = draft.baseUrl.trim().replace(/\/+$/, "");
    if (base !== (ov.baseUrlOverridden ? ov.baseUrl : "")) body.baseUrl = base;
    if (draft.key.trim()) body.key = draft.key.trim();
    const models = {};
    let touched = false;
    ov.models.forEach((m) => {
      const want = (draft.models[m.purpose] || "").trim();
      if (want !== (m.overridden ? m.value : "")) { models[m.purpose] = want; touched = true; }
    });
    if (touched) body.models = models;
    if (String(draft.temperature).trim() !== String(ov.params?.temperature || "").trim()) body.temperature = draft.temperature.trim();
    if (String(draft.maxTokens).trim() !== String(ov.params?.maxTokens || "").trim()) body.maxTokens = draft.maxTokens.trim();
    try {
      await api.put("/admin/ai", body);
      toast.success(zh ? "已保存，即时生效（无需重启）" : "Saved, effective immediately");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed"));
    }
    setBusy(false);
  };

  const clearKey = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put("/admin/ai", { key: "__clear__" });
      toast.success(zh ? "已恢复为环境变量密钥" : "Restored to env key");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed"));
    }
    setBusy(false);
  };

  const runTest = async () => {
    if (testing) return;
    setTesting(true);
    setTest(null);
    try {
      const { data } = await api.post("/admin/ai/test");
      setTest({ ok: true, ...data });
    } catch (e) {
      setTest({ ok: false, detail: e?.detailText || e.message || "Failed" });
    }
    setTesting(false);
  };

  const srcLabel = (s) => (SOURCE_LABEL[s] || {})[zh ? "zh" : "en"] || s;

  return (
    <div className="space-y-4" data-testid="ai-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Bot className="h-6 w-6 text-indigo-600" />{zh ? "AI 配置" : "AI Settings"}
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">
          {zh ? "渠道网关、密钥、模型映射与参数集中配置；后台填写的值覆盖环境变量，保存后即时生效。" : "Gateway, key, model mapping and parameters in one place; saved values override env vars and take effect immediately."}
        </p>
      </div>

      {!ov && !err && <div className="py-10 text-center text-slate-400" data-testid="ai-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
      {err && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="ai-error">
          <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-800 flex-1 min-w-0">{zh ? `AI 配置加载失败：${err}` : `Failed to load AI settings: ${err}`}</p>
          <Button variant="outline" size="sm" onClick={load} className={FOCUS}>{zh ? "重试" : "Retry"}</Button>
        </div>
      )}

      {ov && draft && (
        <>
          <Section testId="ai-gateway" title={zh ? "渠道网关" : "Gateway"} icon={Server}
            badge={<StatusBadge ok={!!ov.baseUrl} zh={zh} okText={zh ? "已生效" : "Active"} badText={zh ? "未指定" : "Unset"} />}
            hint={srcLabel(ov.baseUrlSource)}>
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs text-slate-500">{zh ? "当前生效" : "Currently active"}</p>
                <p className="mt-0.5 text-xs font-mono text-slate-800 break-all" data-testid="ai-gateway-effective">{ov.baseUrl || (zh ? "（SDK 默认）" : "(SDK default)")}</p>
              </div>
              <Field label={zh ? "自定义网关地址" : "Custom gateway URL"} hint={zh ? "留空 = 用默认" : "blank = default"} testId="ai-gateway-field">
                <input type="text" value={draft.baseUrl} onChange={(e) => setDraft((d) => ({ ...d, baseUrl: e.target.value }))}
                  placeholder={ov.baseUrl || ov.emergentDefault} autoComplete="off" data-testid="ai-gateway-input"
                  className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono ${FOCUS}`} />
              </Field>
              <p className="text-xs text-slate-400 leading-relaxed">
                {zh ? `内置默认：${ov.emergentDefault}。填写后会覆盖它；清空即恢复默认。` : `Built-in default: ${ov.emergentDefault}. Filling this overrides it; clear to revert.`}
              </p>
            </div>
          </Section>

          <Section testId="ai-key" title={zh ? "密钥" : "API Key"} icon={KeyRound}
            badge={<StatusBadge ok={ov.key.set} zh={zh} />}
            hint={`${zh ? "来源" : "source"} ${(KEY_SOURCE_LABEL[ov.key.source] || {})[zh ? "zh" : "en"] || ov.key.source}`}>
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-xs text-slate-500">{zh ? "当前" : "Current"}</span>
                <span className="text-xs font-mono text-slate-800" data-testid="ai-key-masked">
                  {ov.key.masked || (ov.key.set ? (zh ? "已配置（无权查看脱敏值）" : "configured") : (zh ? "未配置" : "not set"))}
                </span>
              </div>
              <Field label={zh ? "更新密钥" : "Update key"} hint={zh ? "留空 = 不修改" : "blank = keep"} testId="ai-key-field">
                <input type="password" value={draft.key} onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))}
                  placeholder={zh ? "粘贴新的 API Key" : "Paste new API key"} autoComplete="new-password" data-testid="ai-key-input"
                  className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono ${FOCUS}`} />
              </Field>
              <div className="flex flex-wrap items-center gap-3">
                {ov.key.source === "db" && (
                  <button type="button" onClick={clearKey} disabled={busy} data-testid="ai-key-clear"
                    className={"max-md:min-h-11 max-md:min-w-11 " + (`text-[11px] text-slate-500 underline hover:text-slate-700 disabled:opacity-40 ${FOCUS}`)}>
                    {zh ? "恢复环境变量密钥" : "Restore env key"}
                  </button>
                )}
                {!ov.key.encryptionReady && (
                  <span className="text-[11px] text-amber-600">{zh ? "缺少加密密钥（ONBOARD_ENC_KEY / JWT_SECRET），暂不能保存密钥" : "No encryption key configured"}</span>
                )}
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                {zh ? "密钥加密落库，页面只回显脱敏值，永不回传完整密钥。" : "The key is encrypted at rest; only a masked value is ever returned."}
              </p>
            </div>
          </Section>

          <Section testId="ai-models" title={zh ? "模型映射" : "Model mapping"} icon={Boxes}
            hint={zh ? "按用途指定，留空 = 用默认" : "per purpose; blank = default"}>
            <div className="space-y-3">
              {ov.models.map((m) => (
                <Field key={m.purpose} label={m.label} hint={m.purpose} testId={`ai-model-${m.purpose}`}>
                  <div className="flex items-center gap-2">
                    <input type="text" value={draft.models[m.purpose] ?? ""}
                      onChange={(e) => setDraft((d) => ({ ...d, models: { ...d.models, [m.purpose]: e.target.value } }))}
                      placeholder={m.value} autoComplete="off" data-testid={`ai-model-input-${m.purpose}`}
                      className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono ${FOCUS}`} />
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${m.overridden ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>
                      {m.overridden ? (zh ? "后台" : "db") : (zh ? "默认" : "default")}
                    </span>
                  </div>
                </Field>
              ))}
              <p className="text-xs text-slate-400 leading-relaxed">
                {zh ? "格式为 模型名 或 provider/模型名（例如 provider/model-name）。改动会同时影响该用途下的所有功能。" : "Format: model name or provider/model (e.g. provider/model-name). Changes affect every feature using that purpose."}
              </p>
            </div>
          </Section>

          <Section testId="ai-params" title={zh ? "参数" : "Parameters"} icon={SlidersHorizontal}>
            <div className="space-y-3">
              <Field label={zh ? "温度 temperature" : "Temperature"} hint="0–2" testId="ai-param-temperature">
                <input type="text" value={draft.temperature} onChange={(e) => setDraft((d) => ({ ...d, temperature: e.target.value }))}
                  placeholder={zh ? "留空 = 不传" : "blank = omit"} autoComplete="off" data-testid="ai-param-temperature-input"
                  className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono ${FOCUS}`} />
              </Field>
              <Field label={zh ? "最大输出 max_tokens" : "Max tokens"} hint="1–200000" testId="ai-param-maxtokens">
                <input type="text" value={draft.maxTokens} onChange={(e) => setDraft((d) => ({ ...d, maxTokens: e.target.value }))}
                  placeholder={zh ? "留空 = 不传" : "blank = omit"} autoComplete="off" data-testid="ai-param-maxtokens-input"
                  className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono ${FOCUS}`} />
              </Field>
              <p className="text-xs text-slate-400 leading-relaxed">
                {zh ? "作为全局默认值，仅在调用方未显式指定时生效，不会覆盖各功能自己的设置。" : "Applied as a global default only when a caller does not set its own."}
              </p>
            </div>
          </Section>

          <Section testId="ai-actions" title={zh ? "保存与测试" : "Save and test"} icon={PlugZap}>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="ai-save" className={`bg-indigo-600 text-white ${FOCUS}`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}
              </Button>
              <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="ai-reload" className={FOCUS}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />{zh ? "重新加载" : "Reload"}
              </Button>
              <Button size="sm" variant="outline" onClick={runTest} disabled={testing} data-testid="ai-test" className={FOCUS}>
                {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Activity className="h-3.5 w-3.5 mr-1" />}{zh ? "测试连通性" : "Test"}
              </Button>
              {!ov.canEdit && <span className="text-xs text-amber-600">{zh ? "只读：仅管理员 / CEO 可修改" : "Read-only: admin / CEO only"}</span>}
            </div>
            {test && (
              <div role="status" data-testid="ai-test-result"
                className={`mt-3 flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-xs ${test.ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                {test.ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                {test.ok
                  ? <span className="break-all">{zh ? `连通正常 · 模型 ${test.model} · 回复「${test.reply}」` : `OK · ${test.model} · "${test.reply}"`}</span>
                  : <span className="break-all">{test.detail}</span>}
              </div>
            )}
          </Section>

          {Array.isArray(ov.usage) && ov.usage.length > 0 && (
            <Section testId="ai-usage" title={zh ? "影响范围" : "Affected features"} icon={ListChecks}>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {ov.usage.map((u, i) => (
                  <li key={`${u.purpose}-${i}`} className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                    <span className="flex-1 min-w-0">{u.label}</span>
                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 font-mono">{u.purpose}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
